import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  MAX_GROUP_LEVELS,
  groupByOf,
  groupingOf,
  levelLabel,
  toggleLevel,
  type ReportGroupLevel,
} from '../lib/report-builder-state';

/**
 * The Reports screen's Group By: one dropdown, several picks, and the order of
 * the picks is the hierarchy.
 *
 * This replaces a two-option select that could hold one value. The ticket asks
 * for Tempo's model — pick Person, then add Project, and the report groups by
 * person with projects nested underneath; pick them the other way round and it
 * groups by project with people underneath. The FIRST pick is the top-level
 * row, which is what the order of `value` records. [HW-877]
 *
 * Two rules the control enforces rather than documents. The last pick cannot
 * be removed, because a report grouped by nothing is not a report; that refusal
 * is `toggleLevel`'s — the same function the Builder's chips use — which hands
 * back the unchanged state, so the box stays ticked, and its tooltip says why.
 * And a third pick would be refused rather than pushing one out, because
 * silently dropping a level the reader chose is how a table starts answering a
 * different question; that one the control pre-empts by greying the unpicked
 * options at capacity, so `toggleLevel`'s own refusal is a backstop it never
 * reaches. With the two dimensions this screen offers today, capacity is "both
 * ticked" and there is nothing left to grey — the rule waits for a third
 * dimension.
 *
 * The arrow line under the control says the hierarchy back in words — "Person
 * → Project" — because on a list of ticked boxes the order of the ticks is
 * invisible, and that order is the whole point.
 */
export function GroupByPicker({
  value,
  options,
  onChange,
  className,
}: {
  /** Outer → inner, in the order the reader picked them. */
  value: readonly ReportGroupLevel[];
  options: ReadonlyArray<{ level: ReportGroupLevel; label: string }>;
  onChange: (next: ReportGroupLevel[]) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const grouping = groupingOf([...value]);
  const atCapacity = value.length >= MAX_GROUP_LEVELS;

  const handleToggle = (level: ReportGroupLevel) => {
    const next = groupByOf(toggleLevel(grouping, level));
    // `toggleLevel` hands back the same state when it refuses — unticking the
    // only pick is the case this control can reach — and no change means
    // nothing to announce.
    if (next.length !== value.length || next.some((l, i) => l !== value[i])) {
      onChange(next as ReportGroupLevel[]);
    }
  };

  const summary = value.map((level) => levelLabel(level)).join(' → ');

  return (
    // The hint hangs below the field rather than in the flow: in a filter row
    // aligned to its bottom edge, an in-flow line made this column taller than
    // its neighbours and lifted the field above theirs.
    <div className={cn('relative', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="Group by"
            className="h-9 w-[220px] justify-between text-sm font-normal"
          >
            <span className="truncate">{summary}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-0" align="start">
          <Command>
            <CommandList>
              <CommandGroup>
                {options.map(({ level, label }) => {
                  const picked = value.includes(level);
                  const position = value.indexOf(level);
                  // Unpicked options go inert at two picks: the schema allows no
                  // third level, and greying the box says so before the click
                  // rather than ignoring it after.
                  const disabled = !picked && atCapacity;
                  // The only picked option cannot be unpicked; the box stays
                  // ticked and says why.
                  const lastOne = picked && value.length === 1;
                  return (
                    <CommandItem
                      key={level}
                      value={level}
                      disabled={disabled}
                      onSelect={() => handleToggle(level)}
                      title={
                        lastOne
                          ? 'A report needs at least one grouping'
                          : disabled
                            ? 'At most two groupings'
                            : undefined
                      }
                      className={cn('gap-2', lastOne && 'cursor-default')}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                          // Ticked like the site's checkbox: a mint fill. [HW-926]
                          picked ? 'border-cta bg-cta text-cta-foreground' : 'opacity-50'
                        )}
                        aria-hidden="true"
                      >
                        {picked && <Check className="h-3 w-3" />}
                      </span>
                      <span className="flex-1">{label}</span>
                      {/* Which row of the hierarchy this pick is — 1 for the
                          top level, 2 for the nested one — so the order of the
                          ticks is readable inside the list as well as under it. */}
                      {picked && (
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {position + 1}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <p
        className="absolute left-0 top-full mt-1 whitespace-nowrap text-xs text-muted-foreground"
        aria-live="polite"
      >
        {value.length > 1 ? (
          <>
            <span className="font-medium text-foreground">{levelLabel(value[0])}</span>
            {' → '}
            {levelLabel(value[1])}
            <span className="ml-1">— first pick is the top level</span>
          </>
        ) : (
          <>Add a second grouping to nest it underneath</>
        )}
      </p>
    </div>
  );
}
